import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react'

import { ApiError } from '@/lib/api'

import { brandingApi, type BrandingOptions, type BrandingSettings } from './api'
import { timezoneOptions } from './constants'
import {
  useBrandingAssetPreview,
  useBrandingOptions,
  useBrandingSettings,
  useRemoveFavicon,
  useRemoveLogo,
  useUpdateBrandingSettings,
  useUploadFavicon,
  useUploadLogo,
} from './useBrandingSettings'

interface FormState {
  display_name: string
  timezone: string
  currency: string
  locale: string
  support_email: string
  sender_name: string
  sender_email: string
}

function toFormState(settings: BrandingSettings): FormState {
  return {
    display_name: settings.display_name ?? '',
    timezone: settings.timezone,
    currency: settings.currency,
    locale: settings.locale,
    support_email: settings.support_email ?? '',
    sender_name: settings.sender_name ?? '',
    sender_email: settings.sender_email ?? '',
  }
}

type AssetKind = 'logo' | 'favicon'

function assetRules(kind: AssetKind, options: BrandingOptions) {
  return kind === 'logo'
    ? { allowed: options.logo_content_types, maxBytes: options.logo_max_bytes, label: 'Logo' }
    : {
        allowed: options.favicon_content_types,
        maxBytes: options.favicon_max_bytes,
        label: 'Favicon',
      }
}

/**
 * The tenancy card's branding and locale settings screen (03 §3.3):
 * company name, logo, favicon, timezone, currency, locale, support contact
 * and email sender identity, all editable in one place, with the computed
 * footer previewed at the bottom.
 */
export function BrandingSettingsPage() {
  const settings = useBrandingSettings()
  const options = useBrandingOptions()
  const update = useUpdateBrandingSettings()
  const uploadLogo = useUploadLogo()
  const removeLogo = useRemoveLogo()
  const uploadFavicon = useUploadFavicon()
  const removeFavicon = useRemoveFavicon()

  const [form, setForm] = useState<FormState | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [assetError, setAssetError] = useState<string | null>(null)

  useEffect(() => {
    if (settings.data && form === null) setForm(toFormState(settings.data))
  }, [settings.data, form])

  const logoUrl = useBrandingAssetPreview(
    brandingApi.logoObjectUrl,
    settings.data?.has_logo ?? false,
    settings.dataUpdatedAt,
  )
  const faviconUrl = useBrandingAssetPreview(
    brandingApi.faviconObjectUrl,
    settings.data?.has_favicon ?? false,
    settings.dataUpdatedAt,
  )

  if (settings.isPending || options.isPending || form === null) return <p>Loading…</p>
  if (settings.isError || options.isError) return <p role="alert">Could not load branding settings.</p>

  const data = settings.data
  const opts = options.data
  const currentForm = form

  function field(key: keyof FormState) {
    return (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setForm((current) => (current ? { ...current, [key]: event.target.value } : current))
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setFormError(null)
    try {
      await update.mutateAsync([
        {
          display_name: currentForm.display_name,
          timezone: currentForm.timezone,
          currency: currentForm.currency,
          locale: currentForm.locale,
          support_email: currentForm.support_email || undefined,
          sender_name: currentForm.sender_name || undefined,
          sender_email: currentForm.sender_email || undefined,
        },
      ])
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : 'Could not save settings.')
    }
  }

  async function handleAsset(kind: AssetKind, file: File) {
    setAssetError(null)
    const rules = assetRules(kind, opts)
    if (!rules.allowed.includes(file.type)) {
      setAssetError(`${rules.label} must be one of: ${rules.allowed.join(', ')}`)
      return
    }
    if (file.size > rules.maxBytes) {
      setAssetError(`${rules.label} is too large (maximum ${Math.round(rules.maxBytes / 1024)} KB).`)
      return
    }
    try {
      if (kind === 'logo') await uploadLogo.mutateAsync([file])
      else await uploadFavicon.mutateAsync([file])
    } catch (error) {
      setAssetError(error instanceof ApiError ? error.message : `Could not upload the ${kind}.`)
    }
  }

  function fileInput(kind: AssetKind) {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      event.target.value = ''
      if (file) void handleAsset(kind, file)
    }
  }

  return (
    <section aria-label="Branding and locale settings">
      <h1>Branding &amp; locale</h1>
      <form onSubmit={(event) => void handleSubmit(event)}>
        <label>
          Company name
          <input value={currentForm.display_name} onChange={field('display_name')} required />
        </label>
        <label>
          Timezone
          <select value={currentForm.timezone} onChange={field('timezone')}>
            {timezoneOptions().map((timezone) => (
              <option key={timezone} value={timezone}>
                {timezone}
              </option>
            ))}
          </select>
        </label>
        <label>
          Currency
          <select value={currentForm.currency} onChange={field('currency')}>
            {opts.currencies.map((currency) => (
              <option key={currency} value={currency}>
                {currency}
              </option>
            ))}
          </select>
        </label>
        <label>
          Locale
          <select value={currentForm.locale} onChange={field('locale')}>
            {opts.locales.map((locale) => (
              <option key={locale} value={locale}>
                {locale}
              </option>
            ))}
          </select>
        </label>
        <label>
          Support contact email
          <input type="email" value={currentForm.support_email} onChange={field('support_email')} />
        </label>
        <label>
          Email sender name
          <input value={currentForm.sender_name} onChange={field('sender_name')} />
        </label>
        <label>
          Email sender address
          <input type="email" value={currentForm.sender_email} onChange={field('sender_email')} />
        </label>
        {formError && <p role="alert">{formError}</p>}
        <button type="submit" disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save settings'}
        </button>
      </form>

      <div aria-label="Logo">
        <h2>Logo</h2>
        {logoUrl ? (
          <img src={logoUrl} alt="Tenant logo" data-testid="logo-preview" />
        ) : (
          <p>No logo set.</p>
        )}
        <input
          type="file"
          aria-label="Upload logo"
          accept={opts.logo_content_types.join(',')}
          onChange={fileInput('logo')}
        />
        {data?.has_logo && (
          <button type="button" onClick={() => void removeLogo.mutateAsync([])}>
            Remove logo
          </button>
        )}
      </div>

      <div aria-label="Favicon">
        <h2>Favicon</h2>
        {faviconUrl ? (
          <img src={faviconUrl} alt="Tenant favicon" data-testid="favicon-preview" />
        ) : (
          <p>No favicon set.</p>
        )}
        <input
          type="file"
          aria-label="Upload favicon"
          accept={opts.favicon_content_types.join(',')}
          onChange={fileInput('favicon')}
        />
        {data?.has_favicon && (
          <button type="button" onClick={() => void removeFavicon.mutateAsync([])}>
            Remove favicon
          </button>
        )}
      </div>

      {assetError && <p role="alert">{assetError}</p>}

      <p data-testid="footer-preview">{data?.footer_text}</p>
    </section>
  )
}
